import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { HeadlineM, LabelM } from '@sorare/core/src/atoms/typography';
import { DialogKey } from '@sorare/core/src/components/navigation/WithDialogs';
import type { StuffInRewardBoxType } from '@sorare/core/src/components/rewards/RewardBoxDialog/types';
import { RewardCount } from '@sorare/core/src/components/rewards/RewardCount';
import { DeliverableItemOrderFooter } from '@sorare/core/src/components/rewards/deliverableItem/DeliverableItemOrderFooter';
import { useQuery } from '@sorare/core/src/hooks/graphql/useQuery';
import { useOpenDialog } from '@sorare/core/src/hooks/navigation/useOpenDialog';
import { isType } from '@sorare/core/src/lib/gql';
import { lazy } from '@sorare/core/src/lib/retry';

import { deliverableItemOrderFragment } from 'components/rewards/deliverableItem/DeliverableItemOrderFormDialog';

import type {
  DeliverableItemInRewardBox_CurrentUser,
  DeliverableItemInRewardBox_ProbabilisticBundleSlotItemInterface,
  DeliverableItemInRewardBox_ProbabilisticBundleSlotItemInterface_ProbabilisticBundleSlotDeliverableItem_,
  UnclaimedDeliveryItemOrders,
  UnclaimedDeliveryItemOrdersVariables,
} from './__generated__/index.graphql';

const JerseyInRewardBox = lazy(
  async () =>
    import('@sorare/trois/src/components/RewardBox/JerseyInRewardBox'),
  'JerseyInRewardBox'
);

const ShopItemInRewardBoxTrois = lazy(
  async () =>
    import('@sorare/trois/src/components/RewardBox/ShopItemInRewardBox'),
  'ShopItemInRewardBox'
);

const DescriptionRoot = styled(Vertical)`
  width: max-content;
  white-space: normal;
`;

const currentUserFragment = gql`
  fragment DeliverableItemInRewardBox_CurrentUser on CurrentUser {
    slug
    id
    unclaimedDeliverableItemOrders(deliverableItemId: $deliverableItemId) {
      id
      ...DeliverableItemOrderFormDialog_deliverableItemOrderFragment
    }
  }
  ${deliverableItemOrderFragment}
` as TypedDocumentNode<DeliverableItemInRewardBox_CurrentUser>;

const UNCLAIMED_DELIVERY_ITEM_ORDERS = gql`
  query UnclaimedDeliveryItemOrders($deliverableItemId: ID!) {
    currentUser {
      slug
      ...DeliverableItemInRewardBox_CurrentUser
    }
  }
  ${currentUserFragment}
` as TypedDocumentNode<
  UnclaimedDeliveryItemOrders,
  UnclaimedDeliveryItemOrdersVariables
>;

type Props = {
  item: DeliverableItemInRewardBox_ProbabilisticBundleSlotItemInterface;
  visible: boolean;
};

const DeliverableItemInRewardBoxCanHandle = (
  item: Props['item']
): item is DeliverableItemInRewardBox_ProbabilisticBundleSlotItemInterface_ProbabilisticBundleSlotDeliverableItem_ => {
  return isType(item, 'ProbabilisticBundleSlotDeliverableItem');
};

const Description = ({
  id,
  quantity,
  name,
}: {
  id: string;
  quantity: number;
  name: string;
}) => {
  const openDialog = useOpenDialog();

  const { data } = useQuery(UNCLAIMED_DELIVERY_ITEM_ORDERS, {
    variables: {
      deliverableItemId: id,
    },
    fetchPolicy: 'cache-first',
  });

  const deliverableItemOrder =
    data?.currentUser?.unclaimedDeliverableItemOrders[0];

  return (
    <DescriptionRoot gap={2}>
      <HeadlineM as="span">
        <RewardCount quantity={quantity} rewardName={name} />
      </HeadlineM>
      {!!deliverableItemOrder && (
        <DeliverableItemOrderFooter
          onClick={() => {
            openDialog(DialogKey.deliveryItemOrder, deliverableItemOrder.id);
          }}
        >
          <LabelM as="span">
            <FormattedMessage
              id="DeliverableItemFooter.timeLimit"
              defaultMessage="or you can do it within the next 15 days"
            />
          </LabelM>
        </DeliverableItemOrderFooter>
      )}
    </DescriptionRoot>
  );
};

export const DeliverableItemInRewardBox: StuffInRewardBoxType<
  DeliverableItemInRewardBox_ProbabilisticBundleSlotItemInterface
> = ({ item, visible }: Props) => {
  if (!DeliverableItemInRewardBoxCanHandle(item)) {
    return null;
  }

  const { rewardDeliverableItem } = item;
  const { deliverableItem } = rewardDeliverableItem || {};

  if (!rewardDeliverableItem || !deliverableItem) {
    return null;
  }
  const description = (
    <Description
      id={deliverableItem.id}
      name={deliverableItem.name}
      quantity={rewardDeliverableItem.quantity}
    />
  );

  if (isType(deliverableItem, 'JerseyDeliverableItem')) {
    return (
      <JerseyInRewardBox
        visible={visible}
        teamLogoUrl={deliverableItem.team?.pictureUrl || undefined}
        colors={
          deliverableItem.team?.styleGuideColor
            ? [deliverableItem.team?.styleGuideColor]
            : undefined
        }
        signed={deliverableItem.signed}
        description={description}
      />
    );
  }

  return (
    <ShopItemInRewardBoxTrois
      visible={visible}
      url={deliverableItem.threeDimensionalAssetUrl}
      pictureUrl={deliverableItem.pictureUrl}
      quantity={rewardDeliverableItem.quantity}
      description={description}
    />
  );
};

DeliverableItemInRewardBox.fragments = {
  ProbabilisticBundleSlotItemInterface: gql`
    fragment DeliverableItemInRewardBox_ProbabilisticBundleSlotItemInterface on ProbabilisticBundleSlotItemInterface {
      id
      ... on ProbabilisticBundleSlotDeliverableItem {
        id
        rewardDeliverableItem {
          quantity
          deliverableItem {
            id
            slug
            name
            mandatoryWearableSizeInput
            pictureUrl
            threeDimensionalAssetUrl
            ... on JerseyDeliverableItem {
              slug
              id
              signed
              team: anyTeam {
                slug
                pictureUrl
                styleGuideColor
              }
            }
          }
        }
      }
    }
  ` as TypedDocumentNode<DeliverableItemInRewardBox_ProbabilisticBundleSlotItemInterface>,
};

const DeliverableItemInRewardBoxPreloader = () => {
  return null;
};

DeliverableItemInRewardBox.preloader = DeliverableItemInRewardBoxPreloader;

DeliverableItemInRewardBox.canHandle = DeliverableItemInRewardBoxCanHandle;

DeliverableItemInRewardBox.description = (item: Props['item']) => {
  if (!DeliverableItemInRewardBoxCanHandle(item)) {
    return null;
  }

  if (!item.rewardDeliverableItem) {
    return '';
  }

  const {
    quantity,
    deliverableItem: { name },
  } = item.rewardDeliverableItem;

  if (quantity > 1) {
    return `${quantity}× ${name}`;
  }
  return name;
};

DeliverableItemInRewardBox.fallback = (item: Props['item']) => {
  if (!DeliverableItemInRewardBoxCanHandle(item)) {
    return null;
  }
  return <div>{DeliverableItemInRewardBox.description(item)}</div>;
};
